import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/Toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import StatusBadge from '../../components/StatusBadge';
import { styles, colors, radius } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const SiparisDetayPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: siparisId } = useParams();

  const [siparis, setSiparis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNotes, setEditingNotes] = useState(false);
  const [tempNotlar, setTempNotlar] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (!siparisId) {
      navigate('/siparisler');
      return;
    }

    fetch(`${API_URL}/api/siparisler/${siparisId}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setSiparis(data);
        setLoading(false);
      })
      .catch(err => {
        logError(err);
        navigate('/siparisler');
      });
  }, [siparisId, navigate]);

  if (loading && !siparis) return <div>Yükleniyor...</div>;
  if (!siparis) return <div>Sipariş bulunamadı</div>;

  return (
    <div>
      <Header title={`Sipariş Detayi: ${siparis.siparis_no}`} />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/siparisler')}
            style={{ padding: '10px 15px', background: '#ddd', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}
          >
            Listeye Don
          </button>
          <button
            onClick={async () => {
              try {
                const response = await fetch(`${API_URL}/api/siparisler/${siparisId}/pdf`, { headers: getAuthHeaders() });
                if (!response.ok) throw new Error('PDF olusturulamadi');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `siparis_${siparis.siparis_no}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                toast.success('PDF başarıyla indirildi!');
              } catch (err) {
                toast.error('PDF indirilemedi: ' + err.message);
              }
            }}
            style={{
              padding: '10px 20px',
              background: '#d63031',
              color: 'white',
              border: 'none',
              borderRadius: radius.sm,
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            PDF Indir
          </button>

          {/* Sipariş Ver butonu: sadece satinalma/yonetim/admin ve ONAYLANDI durumunda */}
          {['satinalma', 'yonetim', 'admin'].includes(user?.role) &&
           siparis.durum === 'ONAYLANDI' && (
            <button
              onClick={async () => {
                if (!window.confirm('Bu siparisi tedarikciye vermek istediğinize emin misiniz?')) return;
                try {
                  const response = await fetch(`${API_URL}/api/siparisler/${siparisId}/siparis-verdim`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
                  });
                  if (!response.ok) {
                    const err = await response.json();
                    throw new Error(err.detail || 'İşlem başarısız');
                  }
                  setSiparis({ ...siparis, durum: 'SIPARIS_VERILDI', approval_status: 'ONAYLANDI' });
                  invalidateQueries(['siparis', 'dashboard', 'talep']);
                  toast.success('Sipariş başarıyla verildi!');
                } catch (err) {
                  toast.error('Hata: ' + err.message);
                }
              }}
              style={{
                padding: '10px 20px',
                background: styles.success,
                color: 'white',
                border: 'none',
                borderRadius: radius.sm,
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              Sipariş Ver
            </button>
          )}
        </div>

        {/* Header Info */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div>
            <div style={{ fontSize: '12px', color: colors.textTertiary }}>Tedarikçi</div>
            <div style={{ fontWeight: 'bold' }}>{siparis.tedarikci_ad}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: colors.textTertiary }}>Tarih</div>
            <div style={{ fontWeight: 'bold' }}>{new Date(siparis.siparis_tarihi).toLocaleDateString()}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: colors.textTertiary }}>Durum</div>
            <StatusBadge status={siparis.durum} />
            {siparis.approval_status && siparis.approval_status !== 'ONAYLANDI' && (
              <div style={{ marginTop: '4px' }}>
                <StatusBadge status={siparis.approval_status} />
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: '12px', color: colors.textTertiary }}>Genel Toplam (KDV Dahil)</div>
            <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{siparis.genel_toplam?.toLocaleString()}</div>
          </div>
        </div>

        {/* Items Table */}
        <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Malzeme</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Miktar</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Birim Fiyat</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Tutar</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Teslim Edilen</th>
              </tr>
            </thead>
            <tbody>
              {siparis.kalemler && siparis.kalemler.map(k => (
                <tr key={k.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '10px' }}>{k.malzeme_ad}</td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>{k.miktar} {k.malzeme_birim}</td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>{k.birim_fiyat}</td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>{k.tutar}</td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>{k.teslim_miktar}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sipariş Notlari Bolumu */}
        <div style={{ background: colors.surface, borderRadius: radius.md, padding: '20px', marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: styles.secondary }}>Sipariş Notlari</h3>
            {!editingNotes && (
              <button
                onClick={() => {
                  setEditingNotes(true);
                  setTempNotlar(siparis.notlar || '');
                }}
                style={{
                  padding: '8px 16px',
                  background: styles.info,
                  color: 'white',
                  border: 'none',
                  borderRadius: radius.sm,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Düzenle
              </button>
            )}
          </div>

          {editingNotes ? (
            <div>
              <textarea
                value={tempNotlar}
                onChange={(e) => setTempNotlar(e.target.value)}
                placeholder="Sipariş hakkında not ekleyin..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: radius.sm,
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/siparisler/${siparisId}/notlar`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                        body: JSON.stringify({ notlar: tempNotlar })
                      });

                      if (response.ok) {
                        setSiparis({ ...siparis, notlar: tempNotlar });
                        invalidateQueries(['siparis', 'dashboard', 'talep']);
                        setEditingNotes(false);
                        toast.success('Notlar kaydedildi!');
                      } else {
                        toast.error('Not kaydedilemedi');
                      }
                    } catch (err) {
                      toast.error('Bağlantı hatası');
                    }
                  }}
                  style={{
                    padding: '10px 20px',
                    background: styles.success,
                    color: 'white',
                    border: 'none',
                    borderRadius: radius.sm,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Kaydet
                </button>
                <button
                  onClick={() => {
                    setEditingNotes(false);
                    setTempNotlar('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#e0e0e0',
                    color: '#333',
                    border: 'none',
                    borderRadius: radius.sm,
                    cursor: 'pointer'
                  }}
                >
                  İptal
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              padding: siparis.notlar ? '15px' : '12px',
              background: siparis.notlar ? '#f8f9fa' : '#fff',
              borderRadius: radius.sm,
              border: `1px solid ${colors.border}`,
              minHeight: '60px',
              color: siparis.notlar ? '#333' : '#999',
              fontStyle: siparis.notlar ? 'normal' : 'italic'
            }}>
              {siparis.notlar || 'Not eklenmemis. Düzenle butonuna tiklayarak not ekleyebilirsiniz.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiparisDetayPage;
